import api from 'api'
import { AuthResponse, SendOtpResponse } from 'models/response'
import { startRegistration, startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser'
import { partnerConsentRequestIn } from 'utils/partner-consent-request'

/**
 * Builds Axios config that sends the Turnstile token via BOTH the header
 * and a `turnstileToken` field merged into the request body.
 *
 * Why both? Cloudflare's edge proxy can strip `cf-*` headers before they
 * reach the origin, especially on Safari mobile. The backend already has a
 * fallback that reads `turnstileToken` from the JSON body when the header
 * is missing (see RequireTurnstileAttribute.GetTurnstileTokenAsync).
 */
const turnstileConfig = (turnstileToken?: string) =>
	turnstileToken ? { headers: { 'cf-turnstile-response': turnstileToken }, _turnstileToken: turnstileToken } : {}

/**
 * Wraps api.post to merge the turnstile token into the request body when present.
 */
const postWithTurnstile = <T>(url: string, data: any, turnstileToken?: string) => {
	const config = turnstileConfig(turnstileToken)
	const body = config._turnstileToken ? { ...data, turnstileToken: config._turnstileToken } : data
	const { _turnstileToken: _, ...axiosConfig } = config
	return api.post<T>(url, body, axiosConfig)
}

export const setToken = (token: any): void => {
	if (token) {
		localStorage.setItem('token', token)
	} else {
		// signing out / account switch: never replay one user's pending invite or partner request for the next.
		// First, so a throwing storage call below can't leave the in-memory copy behind.
		clearPendingInvite()
		clearPendingPartnerConsent()
		localStorage.removeItem('token')
	}
}

const JWT_RE = /^[\w-]+\.[\w-]+\.[\w-]*$/

// useAuth stores the token JSON-encoded, setToken stores it bare, and older builds could leave "undefined".
// Anything else is dropped, so one bad value can't break every request until site data is cleared.
export const getToken = (): string | null => {
	const stored = localStorage.getItem('token')
	if (!stored) return null
	try {
		const parsed = JSON.parse(stored)
		if (typeof parsed === 'string' && parsed) return parsed
	} catch {
		if (JWT_RE.test(stored)) return stored
	}
	localStorage.removeItem('token')
	return null
}

export const setRefreshToken = (token: any): void => {
	if (token) {
		localStorage.setItem('refreshToken', token)
	} else {
		localStorage.removeItem('refreshToken')
	}
}

export const getRefreshToken = (): string | any => {
	return localStorage.getItem('refreshToken')
}

// A signed-out user who opens an invite / private-ticket link is bounced through sign-up, login, MFA,
// password reset, social login or an email-confirmation tab. Router state only survives in-app
// navigations within one tab, so the pending invite is also persisted (with a TTL) and replayed by the guards.
const PENDING_INVITE_KEY = 'pendingInvite'
const PENDING_INVITE_PATH_RE = /^\/accept-invite\/[\w-]+(\?.*)?$/
const PENDING_INVITE_TTL_MS = 6 * 60 * 60 * 1000

type PendingInvite = { path: string; savedAt: number }

// Same-tab fallback used ONLY when the localStorage write failed (private mode / quota). When storage works it is
// the single source of truth, so a clear from another tab (logout, completion) can't be shadowed by a stale copy.
let memoryPendingInvite: PendingInvite | null = null

// localStorage events never fire for a key that was never written, so tabs running on the in-memory fallback
// are told explicitly when another tab clears the invite (logout / completion).
const pendingInviteChannel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('pendingInvite')
if (pendingInviteChannel) {
	pendingInviteChannel.onmessage = () => {
		memoryPendingInvite = null
	}
}

const isValidPendingInvite = (value: Partial<PendingInvite> | null): value is PendingInvite =>
	typeof value?.path === 'string' &&
	PENDING_INVITE_PATH_RE.test(value.path) &&
	Number.isFinite(value.savedAt) &&
	Date.now() - (value.savedAt as number) >= 0 &&
	Date.now() - (value.savedAt as number) <= PENDING_INVITE_TTL_MS

export const setPendingInvite = (path: string): void => {
	const entry = { path, savedAt: Date.now() }
	try {
		localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify(entry))
		memoryPendingInvite = null
	} catch {
		// storage unavailable — keep the full path in memory for this tab
		memoryPendingInvite = entry
	}
}

const removeStoredPendingInvite = (): void => {
	try {
		localStorage.removeItem(PENDING_INVITE_KEY)
	} catch {
		// ignore
	}
}

export const clearPendingInvite = (): void => {
	memoryPendingInvite = null
	pendingInviteChannel?.postMessage('clear')
	removeStoredPendingInvite()
}

const getPendingInvite = (): string | null => {
	let stored: PendingInvite | null = null
	try {
		const raw = localStorage.getItem(PENDING_INVITE_KEY)
		stored = raw ? JSON.parse(raw) : null
	} catch {
		stored = null
	}
	// A failed storage write leaves an older stored invite behind, so prefer whichever valid entry is newer
	const candidates = [stored, memoryPendingInvite].filter(isValidPendingInvite)
	if (candidates.length) {
		candidates.sort((a, b) => b.savedAt - a.savedAt)
		return candidates[0].path
	}
	// drop this tab's expired / malformed stored leftover; a local cleanup must not broadcast a clear to other tabs
	if (stored) removeStoredPendingInvite()
	return null
}

/** Invite id of the pending invite, so the auth guards (which read `location.state.inviteId`) can resume it. */
export const getPendingInviteId = (): string | null => {
	const pending = getPendingInvite()
	return pending ? (pending.split('?')[0].split('/')[2] ?? null) : null
}

/** Query string (incl. leading "?") saved with the pending invite for `inviteId`, or '' when none / a different invite. */
export const getPendingInviteSearch = (inviteId?: string): string => {
	const pending = getPendingInvite()
	if (!pending || !inviteId) return ''
	const [path, ...rest] = pending.split('?')
	return path === `/accept-invite/${encodeURIComponent(inviteId)}` && rest.length ? `?${rest.join('?')}` : ''
}

// A partner's consent request rides in the sign-in page's ?to=, which "Forgot your password?" drops and the reset
// email (a new tab) never had. It is kept briefly, so a later unrelated sign-in on this browser is not sent there.
const PENDING_PARTNER_CONSENT_KEY = 'pendingPartnerConsent'
const PENDING_PARTNER_CONSENT_TTL_MS = 30 * 60 * 1000

type PendingPartnerConsent = { path: string; savedAt: number }

const isValidPendingPartnerConsent = (value: Partial<PendingPartnerConsent> | null): value is PendingPartnerConsent => {
	const age = Date.now() - Number(value?.savedAt)
	return (
		typeof value?.path === 'string' &&
		partnerConsentRequestIn(value.path) !== null &&
		age >= 0 &&
		age <= PENDING_PARTNER_CONSENT_TTL_MS
	)
}

/** Keeps the consent page path (a sign-in `?to=`) for the auth pages that arrive without it. */
export const setPendingPartnerConsent = (path: string): void => {
	try {
		localStorage.setItem(PENDING_PARTNER_CONSENT_KEY, JSON.stringify({ path, savedAt: Date.now() }))
	} catch {
		// storage unavailable: the pages that carry ?to= still work
	}
}

export const clearPendingPartnerConsent = (): void => {
	try {
		localStorage.removeItem(PENDING_PARTNER_CONSENT_KEY)
	} catch {
		// ignore
	}
}

/** The saved consent page path, or null when there is none or it expired. */
export const getPendingPartnerConsentPath = (): string | null => {
	let saved: Partial<PendingPartnerConsent> | null
	try {
		const raw = localStorage.getItem(PENDING_PARTNER_CONSENT_KEY)
		if (!raw) return null
		saved = JSON.parse(raw)
	} catch {
		saved = null
	}
	if (isValidPendingPartnerConsent(saved)) return saved.path
	clearPendingPartnerConsent()
	return null
}

/** Post-auth fallback destination: the pending invite, else the saved partner request, else home. */
export const getPostAuthFallbackPath = (inviteId?: string | null): string => {
	const id = inviteId ?? getPendingInviteId()
	if (id) return `/accept-invite/${id}`
	return getPendingPartnerConsentPath() ?? '/'
}

export const passwordRegister = (data: any, turnstileToken?: string) => {
	return postWithTurnstile<AuthResponse>('/auth/password/register', data, turnstileToken)
}

export const googleExchangeToken = (idToken: string) => {
	return api.post<AuthResponse>('/auth/google/exchange-token', { token: idToken, platform: 'Web' })
}

export const passwordResendRegisterEmail = (data: any) => {
	return api.post<AuthResponse>('/auth/password/resend-register-email', data)
}

export const passwordSignIn = (data: any, turnstileToken?: string) => {
	return postWithTurnstile<AuthResponse>('/auth/password/sign-in', data, turnstileToken)
}

export const passwordImSignIn = (data: any) => {
	return api.post<AuthResponse>('/auth/password/im-sign-in', data)
}

/**
 * One of the accounts a phone number signs in to. `handle` names the account without exposing its id,
 * and is what start/verify use to target it when several accounts share the number.
 */
export type PhoneAccount = {
	handle: string
	maskedEmail?: string | null
	avatarUrl?: string | null
	name?: string | null
}

/** Accounts that sign in with this phone, including ones whose number was never verified. An empty list means none. */
export const phoneAccounts = (data: { phoneNumber: string }, turnstileToken?: string) => {
	return postWithTurnstile<PhoneAccount[]>('/auth/phone/v2/accounts', data, turnstileToken)
}

/** Texts a sign-in code to the phone for the chosen account. */
export const phoneStart = (data: { phoneNumber: string; accountHandle: string }) => {
	return api.post<AuthResponse>('/auth/phone/v2/start', data)
}

/** Exchanges the texted code for a session; the same handle pins the code to the account it was sent for. */
export const phoneVerify = (data: { phoneNumber: string; code: string; accountHandle: string }) => {
	return api.post<AuthResponse>('/auth/phone/v2/verify', data)
}

/** Emails a sign-in code. Answers the same whether or not the address has an account. */
export const emailStart = (data: { emailAddress: string }, turnstileToken?: string) => {
	return postWithTurnstile<SendOtpResponse>('/auth/email/start', data, turnstileToken)
}

/** Exchanges the emailed code for a session; a new address gets its account (isComplete: false) here. */
export const emailVerify = (data: { emailAddress: string; code: string }) => {
	return api.post<AuthResponse>('/auth/email/verify', data)
}

export const verifyOtp = (data: any) => {
	return api.post<AuthResponse>('/auth/password/verify-otp', data)
}

export const resendOtp = (data: any) => {
	return api.post<AuthResponse>('/auth/password/resend-otp', data)
}

export const verifyEmailToken = (data: any) => {
	return api.post<AuthResponse>('/auth/password/verify-email-token', data)
}

export const validateToken = (data: any) => {
	const { token } = data
	return api.post<any>(`/auth/validate-token?token=${token}`)
}

export const checkEmailAvailability = (data: any) => {
	return api.post<any>('/auth/password/check-email-availability', data)
}

export const checkPhoneAvailability = (data: { phoneNumber: string; emailAddress?: string }) => {
	return api.post<boolean>('/auth/password/check-phone-availability', data)
}

export const confirmEmailChange = (data: any) => {
	return api.post<AuthResponse>('/profile/email-change/confirm', data)
}

export const startUpdatePassword = () => {
	return api.post<any>('/auth/password/start-update-password')
}

export const updatePassword = (data: any) => {
	return api.post<any>('/auth/password/update-password', data)
}

export const logout = () => {
	return api.post('/auth/sign-out')
}

export const refresh = (refreshToken: string) => {
	return api.post<AuthResponse>('/auth/refresh', { refreshToken })
}

export const passwordForget = (data: any, turnstileToken?: string) => {
	return postWithTurnstile<any>('/auth/password', data, turnstileToken)
}

export const passwordRecover = (data: any, turnstileToken?: string) => {
	return postWithTurnstile<any>('/auth/password/recover', data, turnstileToken)
}

// Basic API endpoints for passkey
export const passkeyRegisterOptions = (data: any) => {
	return api.post<AuthResponse>('/auth/passkey/register/options', data)
}

export const passkeyRegister = (data: any) => {
	return api.post<AuthResponse>('/auth/passkey/register', data)
}

export const passkeyLoginOptions = (data: any) => {
	return api.post<AuthResponse>('/auth/passkey/login/options', data)
}

export const passkeyLogin = (data: any) => {
	return api.post<AuthResponse>('/auth/passkey/login', data)
}

// Passkey registration flow using @simplewebauthn/browser
export const registerPasskey = async ({
	userName,
	displayName,
}: {
	userName: string
	displayName: string
}): Promise<any> => {
	try {
		// Step 1: Get registration options from server
		const optionsResponse = await passkeyRegisterOptions({ userName, displayName })

		// Extract options and token from response
		const { options, token } = optionsResponse as any

		// Step 2: Use @simplewebauthn/browser to create credential
		// It automatically handles all base64url encoding/decoding
		const credential = await startRegistration(options)

		// Step 3: Convert credential response to format expected by server
		const registrationData = {
			attestationResponse: {
				id: credential.id,
				rawId: credential.rawId,
				type: 0,
				extensions: credential.clientExtensionResults,
				response: {
					attestationObject: credential.response.attestationObject,
					clientDataJSON: credential.response.clientDataJSON,
				},
			},
			token,
		}

		// Step 4: Send registration data to server
		const registrationResponse = await passkeyRegister(registrationData)
		return registrationResponse
	} catch (error: any) {
		throw new Error(`Passkey registration failed: ${error.message}`)
	}
}

// Passkey login flow using @simplewebauthn/browser
export const loginPasskey = async (): Promise<any> => {
	try {
		// Step 1: Get authentication options from server without username
		const optionsResponse = await passkeyLoginOptions({})

		// Check if response has options structure like registration
		const { options, token } = optionsResponse as any

		// Step 2: Use @simplewebauthn/browser to authenticate
		// It automatically handles all base64url encoding/decoding
		const credential = await startAuthentication(options)

		// Step 3: Convert credential response to format expected by server
		const authenticationData = {
			assertionResponse: {
				id: credential.id,
				rawId: credential.rawId,
				type: 0,
				extensions: credential.clientExtensionResults,
				response: {
					authenticatorData: credential.response.authenticatorData,
					clientDataJSON: credential.response.clientDataJSON,
					signature: credential.response.signature,
					userHandle: credential.response.userHandle || null,
				},
			},
			token,
		}

		// Step 4: Send authentication data to server
		const loginResponse = await passkeyLogin(authenticationData)
		return loginResponse
	} catch (error: any) {
		const errorMsg = error.message || error.data || ''
		const friendlyMessage = errorMsg.includes('The operation either timed out or was not allowed')
			? 'Passkey Login unsuccessful. The process was interrupted because it timed out or was rejected. Please try again.'
			: `Passkey Login unsuccessful: ${errorMsg}`

		throw new Error(friendlyMessage)
	}
}

// Check if WebAuthn is supported using @simplewebauthn/browser (SSR safe)
export const isPasskeySupported = (): boolean => {
	// Ensure we're in a browser environment
	if (typeof window === 'undefined' || typeof navigator === 'undefined') {
		return false
	}

	// Use helper from @simplewebauthn/browser
	return browserSupportsWebAuthn()
}
